import { useEffect, useRef, useState } from "react";
import { Download, FileText, Loader2 } from "lucide-react";
import PdfPane from "../components/PdfPane";
import { WorkPackage } from "./types";
import { PackagePreview, fetchPackageFile, getPackagePreview, packageFileUrl } from "./api";
import { sanitizePreviewHtml } from "./sanitizePreviewHtml";

/** Where to send the document. `key` is what makes pressing the same fact twice jump twice. */
export interface DocJump {
  /** The heading a fact was read from — matched against the document's own headings. */
  heading?: string;
  /** A phrase to find, for a PDF, where there are no headings to match. */
  quote?: string;
  key: number;
}

interface PackageDocViewerProps {
  projectId: string;
  pkg: WorkPackage;
  jump?: DocJump;
}

/** Headings are compared loosely: "3.1 Holds List" and "3.1  HOLDS LIST" are the same heading. */
const loose = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/**
 * The document a package was made from, shown beside what was read out of it.
 *
 * A PDF opens in the module's PDF viewer, every page scrollable. Word and Excel come back from the
 * server as plain HTML — headings, paragraphs, tables — which is enough to read a cover block or a
 * material list off; it is not a faithful rendering, and the original is a click away to download.
 *
 * JUMPING TO A FACT. The Documents stage lists what it read with the section and row it came from,
 * and those pointers are worth little if following one is the reader's job. Pressing one sends the
 * pane here: in HTML, to the heading of the section it was read from, flashed so the eye finds it;
 * in a PDF, to the phrase, which is all a PDF offers.
 */
const PackageDocViewer = ({ projectId, pkg, jump }: PackageDocViewerProps) => {
  const [preview, setPreview] = useState<PackagePreview | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const htmlRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    setPreview(null);
    setFile(null);
    setError(null);
    void (async () => {
      try {
        const p = await getPackagePreview(projectId, pkg.id);
        if (!live) return;
        setPreview(p.kind === "html" ? { ...p, html: sanitizePreviewHtml(p.html) } : p);
        if (p.kind === "pdf") {
          const f = await fetchPackageFile(projectId, pkg);
          if (live) setFile(f);
        }
      } catch (err) {
        if (live) setError(err instanceof Error ? err.message : "Could not show the document.");
      }
    })();
    return () => {
      live = false;
    };
  }, [projectId, pkg]);

  // Find the heading the fact was read from and put it at the top of the pane.
  //
  // Depends on jump.key rather than on the heading: pressing the same fact twice after scrolling
  // away should bring you back, and a heading that has not changed would not re-run this.
  useEffect(() => {
    const root = htmlRef.current;
    const heading = jump?.heading;
    if (!root || !heading || preview?.kind !== "html") return;
    const wanted = loose(heading);
    const match = Array.from(root.querySelectorAll("h1, h2, h3, h4, h5, h6, p, td, th")).find(
      (el) => loose(el.textContent ?? "") === wanted,
    );
    if (!match) return;
    match.scrollIntoView({ block: "start", behavior: "smooth" });
    match.classList.add("vr-doc-flash");
    const timer = window.setTimeout(() => match.classList.remove("vr-doc-flash"), 1600);
    return () => window.clearTimeout(timer);
  }, [jump?.key, jump?.heading, preview?.kind]);

  const download = (
    <a className="vr-rowbtn" href={packageFileUrl(projectId, pkg.id)} target="_blank" rel="noreferrer">
      <Download size={12} /> Open original
    </a>
  );

  return (
    <div className="vr-pkg-doc">
      <div className="vr-pkg-doc-head">
        <FileText size={14} />
        <span className="vr-file-name">{pkg.source?.fileName}</span>
        {download}
      </div>

      <div className="vr-pkg-doc-body">
        {error && <div className="vr-form-error">{error}</div>}
        {!error && preview === null && (
          <div className="vr-empty">
            <Loader2 size={16} className="vr-spin-inline" /> Opening the document…
          </div>
        )}
        {preview?.kind === "pdf" && (
          <PdfPane
            file={file}
            loading={!file}
            page={1}
            quote={jump?.quote}
            jumpKey={jump?.key ?? 0}
            continuous
            loadingLabel="Opening the document…"
          />
        )}
        {preview?.kind === "html" && (
          // Built server-side from the Word / Excel file; sanitised by DOMPurify when it arrived (above).
          <div className="vr-doc-html" ref={htmlRef} dangerouslySetInnerHTML={{ __html: preview.html }} />
        )}
        {preview?.kind === "none" && (
          <div className="vr-empty">This file type cannot be shown here. Open the original to read it.</div>
        )}
      </div>
    </div>
  );
};

export default PackageDocViewer;
